import { Link } from '@tanstack/react-router';
import { ChevronRight, ClipboardList } from 'lucide-react';
import { MathText } from '@jeraisonne/ui';
import { AvatarCircle } from '@/components/avatar-circle';
import { formatGradeNumber, formatScore, gradeOn20 } from '@/lib/evaluations/grades';
import { formatRelativeDate } from './format';
import type { EvaluationFeedRow, ViewerRole } from './types';

/** En-tête et lien selon qui regarde (prof : son élève ; élève : lui-même). */
export function evaluationFeedView(
  data: Pick<EvaluationFeedRow, 'student_id' | 'student'>,
  viewerRole: ViewerRole,
  viewerId: string | null | undefined,
): { name: string; verb: string; href: string | null; studentId?: string } {
  if (viewerId && viewerId === data.student_id) {
    return { name: 'Tu', verb: 'as saisi ta note', href: '/student/evaluations' };
  }
  const name = data.student?.display_name?.trim() || 'Un élève';
  if (viewerRole === 'tutor') {
    return {
      name,
      verb: 'a reçu sa note',
      href: '/tutor/students/$studentId',
      studentId: data.student_id,
    };
  }
  return { name, verb: 'a reçu sa note', href: null };
}

/** Couleur de la note selon sa valeur sur 20. */
export function gradeTone(on20: number | null): string {
  if (on20 == null) return 'text-ink-700';
  if (on20 >= 14) return 'text-green-700 ocean:text-green-300';
  if (on20 >= 10) return 'text-ink-900';
  return 'text-error-600';
}

/**
 * Carte du Feed « X a reçu sa note » (migrations 0321-0324) : note saisie par
 * l'élève, visible de lui-même et de son prof lié.
 */
export function EvaluationGradeCard({
  data,
  viewerRole,
  viewerId,
}: {
  data: EvaluationFeedRow;
  viewerRole: ViewerRole;
  viewerId?: string | null;
}) {
  const view = evaluationFeedView(data, viewerRole, viewerId);
  const on20 = gradeOn20(data);
  const body = (
    <div className="flex items-center gap-3 p-4">
      <AvatarCircle
        name={data.student?.display_name ?? view.name}
        src={data.student?.avatar_url ?? null}
        size="sm"
      />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-1.5">
          <ClipboardList className="text-primary-400 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="text-primary-600 font-sans text-[11px] font-semibold uppercase tracking-wider">
            Nouvelle note
          </span>
          {data.graded_at && (
            <span className="text-ink-400 ml-auto shrink-0 text-xs">
              {formatRelativeDate(data.graded_at)}
            </span>
          )}
        </div>
        <p className="text-ink-700 text-sm">
          <span className="text-ink-900 font-semibold">{view.name}</span> {view.verb}
        </p>
        <p className="text-ink-900 truncate text-sm font-semibold">
          <MathText>{data.title}</MathText>
        </p>
        <p className="text-ink-500 truncate text-xs">
          {[
            data.subject,
            data.coefficient !== 1 ? `coef ${formatGradeNumber(data.coefficient)}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className={`font-display text-lg font-extrabold ${gradeTone(on20)}`}>
          {formatScore(data)}
        </p>
        {data.max_score !== 20 && on20 != null && (
          <p className="text-ink-500 text-xs">{formatGradeNumber(on20)} / 20</p>
        )}
      </div>
      {view.href && <ChevronRight className="text-ink-400 h-4 w-4 shrink-0" aria-hidden />}
    </div>
  );

  const cls =
    'border-ink-100 ocean:bg-white/5 block rounded-2xl border bg-white shadow-sm active:scale-[0.99]';
  if (view.href === '/tutor/students/$studentId' && view.studentId) {
    return (
      <Link to="/tutor/students/$studentId" params={{ studentId: view.studentId }} className={cls}>
        {body}
      </Link>
    );
  }
  if (view.href === '/student/evaluations') {
    return (
      <Link to="/student/evaluations" className={cls}>
        {body}
      </Link>
    );
  }
  return <div className={cls}>{body}</div>;
}
